import * as XLSX from "xlsx";

export const BARCODE_OVERALL_STATUSES = [
  "Printed",
  "Awaiting stretch",
  "Awaiting Hortonwood",
  "Awaiting despatch",
  "Completed",
  "Inconsistent",
];

const STAGES = [
  {
    key: "stretch",
    label: "Stretch",
    booleanField: "stretch_scan",
    timestampField: "stretch_scantime",
  },
  {
    key: "hortonwood",
    label: "Hortonwood",
    booleanField: "hortonwood_scan",
    timestampField: "hortonwood_scantime",
  },
  {
    key: "despatch",
    label: "Despatch",
    booleanField: "despatch_scan",
    timestampField: "despatch_scantime",
  },
];

export function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value.toDate === "function") {
    const converted = value.toDate();
    return converted instanceof Date && !Number.isNaN(converted.getTime())
      ? converted
      : null;
  }
  if (Number.isFinite(value.seconds)) {
    const milliseconds =
      value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
    const converted = new Date(milliseconds);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }
  const converted = new Date(value);
  return Number.isNaN(converted.getTime()) ? null : converted;
}

export function formatUkDateTime(value) {
  const date = toDate(value);
  return date ? date.toLocaleString("en-GB") : "";
}

function documentData(document) {
  if (!document) return {};
  if (typeof document.data === "function") return document.data() || {};
  if (document.data && typeof document.data === "object") return document.data;
  return document;
}

function documentId(document) {
  return document?.id || "";
}

function addIssue(issues, field, message) {
  issues.push({ field, message });
}

export function findBarcodeInconsistencies(instanceDocument) {
  const instance = documentData(instanceDocument);
  const issues = [];

  if (instance.stretch_scan === true && instance.Printed !== true) {
    addIssue(issues, "stretch", "Stretch is scanned but Printed is false.");
  }
  if (
    instance.hortonwood_scan === true &&
    instance.stretch_scan !== true
  ) {
    addIssue(
      issues,
      "hortonwood",
      "Hortonwood is scanned before Stretch."
    );
  }
  if (
    instance.despatch_scan === true &&
    instance.hortonwood_scan !== true
  ) {
    addIssue(
      issues,
      "despatch",
      "Despatch is scanned before Hortonwood."
    );
  }

  for (const stage of STAGES) {
    const scanned = instance[stage.booleanField] === true;
    const timestamp = toDate(instance[stage.timestampField]);
    if (scanned && !timestamp) {
      addIssue(
        issues,
        stage.key,
        `${stage.label} is scanned but its timestamp is missing.`
      );
    }
    if (!scanned && timestamp) {
      addIssue(
        issues,
        stage.key,
        `${stage.label} has a timestamp but its scan flag is false.`
      );
    }
  }

  const printedAt = toDate(instance.PrintedAt);
  const stretchAt = toDate(instance.stretch_scantime);
  const hortonwoodAt = toDate(instance.hortonwood_scantime);
  const despatchAt = toDate(instance.despatch_scantime);

  if (printedAt && stretchAt && stretchAt < printedAt) {
    addIssue(
      issues,
      "stretch",
      "Stretch timestamp is earlier than the printed timestamp."
    );
  }
  if (stretchAt && hortonwoodAt && hortonwoodAt < stretchAt) {
    addIssue(
      issues,
      "hortonwood",
      "Hortonwood timestamp is earlier than the Stretch timestamp."
    );
  }
  if (hortonwoodAt && despatchAt && despatchAt < hortonwoodAt) {
    addIssue(
      issues,
      "despatch",
      "Despatch timestamp is earlier than the Hortonwood timestamp."
    );
  }

  return issues;
}

export function calculateOverallStatus(instanceDocument, issues) {
  const instance = documentData(instanceDocument);
  const inconsistencies =
    issues || findBarcodeInconsistencies(instanceDocument);

  if (inconsistencies.length) return "Inconsistent";
  if (instance.despatch_scan === true) return "Completed";
  if (instance.hortonwood_scan === true) return "Awaiting despatch";
  if (instance.stretch_scan === true) return "Awaiting Hortonwood";
  if (instance.Printed === true) return "Awaiting stretch";
  return "Printed";
}

export function flattenBarcodeInstance(parentDocument, instanceDocument) {
  const parent = documentData(parentDocument);
  const instance = documentData(instanceDocument);
  const inconsistencies = findBarcodeInconsistencies(instance);

  return {
    id: `${documentId(parentDocument)}-${documentId(instanceDocument)}`,
    parentId: documentId(parentDocument),
    instanceId: documentId(instanceDocument),
    serialNumber: parent.SerialNumber ?? "",
    productCode: parent.ProductCode ?? "",
    barcodeNumber:
      parent.BarcodeNumber ?? documentId(parentDocument) ?? "",
    quantity: parent.Quantity ?? "",
    program: parent.Program ?? "",
    createdAt: toDate(parent.CreatedAt),
    printed: instance.Printed === true,
    printedAt: toDate(instance.PrintedAt),
    stretchScan: instance.stretch_scan === true,
    stretchScanTime: toDate(instance.stretch_scantime),
    hortonwoodScan: instance.hortonwood_scan === true,
    hortonwoodScanTime: toDate(instance.hortonwood_scantime),
    despatchScan: instance.despatch_scan === true,
    despatchScanTime: toDate(instance.despatch_scantime),
    inconsistencies,
    inconsistencyFields: [
      ...new Set(inconsistencies.map((issue) => issue.field)),
    ],
    overallStatus: calculateOverallStatus(instance, inconsistencies),
    parentFields: { ...parent },
    instanceFields: { ...instance },
  };
}

function dateBoundary(dateInput, endOfDay) {
  if (!dateInput) return null;
  const suffix = endOfDay ? "T23:59:59.999" : "T00:00:00.000";
  const date = new Date(`${dateInput}${suffix}`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function filterBarcodeRows(rows, filters = {}) {
  const serial = String(filters.serialNumber || "").trim().toLowerCase();
  const barcode = String(filters.barcodeNumber || "").trim().toLowerCase();
  const product = String(filters.productCode || "").trim().toLowerCase();
  const printedFrom = dateBoundary(filters.printedFrom, false);
  const printedTo = dateBoundary(filters.printedTo, true);

  return (rows || []).filter((row) => {
    if (
      serial &&
      !String(row.serialNumber || "").toLowerCase().includes(serial)
    ) {
      return false;
    }
    if (
      barcode &&
      !String(row.barcodeNumber || "").toLowerCase().includes(barcode)
    ) {
      return false;
    }
    if (
      product &&
      !String(row.productCode || "").toLowerCase().includes(product)
    ) {
      return false;
    }
    if (printedFrom && (!row.printedAt || row.printedAt < printedFrom)) {
      return false;
    }
    if (printedTo && (!row.printedAt || row.printedAt > printedTo)) {
      return false;
    }
    if (
      filters.overallStatus &&
      row.overallStatus !== filters.overallStatus
    ) {
      return false;
    }
    return true;
  });
}

export function buildMovementTimeline(row) {
  const events = [
    {
      key: "created",
      label: "Parent record created",
      occurred: Boolean(row.createdAt),
      timestamp: row.createdAt,
      inconsistent: false,
    },
    {
      key: "printed",
      label: "Printed",
      occurred: row.printed || Boolean(row.printedAt),
      timestamp: row.printedAt,
      inconsistent: false,
    },
    {
      key: "stretch",
      label: "Stretch scanned",
      occurred: row.stretchScan || Boolean(row.stretchScanTime),
      timestamp: row.stretchScanTime,
      inconsistent: row.inconsistencyFields.includes("stretch"),
    },
    {
      key: "hortonwood",
      label: "Hortonwood scanned",
      occurred: row.hortonwoodScan || Boolean(row.hortonwoodScanTime),
      timestamp: row.hortonwoodScanTime,
      inconsistent: row.inconsistencyFields.includes("hortonwood"),
    },
    {
      key: "despatch",
      label: "Despatch scanned",
      occurred: row.despatchScan || Boolean(row.despatchScanTime),
      timestamp: row.despatchScanTime,
      inconsistent: row.inconsistencyFields.includes("despatch"),
    },
  ].filter((event) => event.occurred);

  return events.sort((a, b) => {
    if (a.timestamp && b.timestamp) return a.timestamp - b.timestamp;
    if (a.timestamp) return -1;
    if (b.timestamp) return 1;
    return 0;
  });
}

export function buildBarcodeTrackingWorkbook(rows) {
  const sheetRows = (rows || []).map((row) => ({
    "Serial Number": String(row.serialNumber || ""),
    "Product Code": String(row.productCode || ""),
    "Barcode Number": String(row.barcodeNumber || ""),
    Quantity: row.quantity,
    Program: row.program,
    "Created At": formatUkDateTime(row.createdAt),
    Printed: row.printed ? "Yes" : "No",
    "Printed At": formatUkDateTime(row.printedAt),
    "Stretch Status": row.stretchScan ? "Completed" : "Not scanned",
    "Stretch Timestamp": formatUkDateTime(row.stretchScanTime),
    "Hortonwood Status": row.hortonwoodScan
      ? "Completed"
      : "Not scanned",
    "Hortonwood Timestamp": formatUkDateTime(row.hortonwoodScanTime),
    "Despatch Status": row.despatchScan ? "Completed" : "Not scanned",
    "Despatch Timestamp": formatUkDateTime(row.despatchScanTime),
    "Overall Status": row.overallStatus,
    Inconsistencies: row.inconsistencies
      .map((issue) => issue.message)
      .join(" | "),
    "Parent Document ID": row.parentId,
    "Instance Document ID": row.instanceId,
  }));

  const worksheet = XLSX.utils.json_to_sheet(sheetRows);
  worksheet["!cols"] = [
    { wch: 18 },
    { wch: 18 },
    { wch: 20 },
    { wch: 10 },
    { wch: 18 },
    { wch: 22 },
    { wch: 10 },
    { wch: 22 },
    { wch: 18 },
    { wch: 22 },
    { wch: 20 },
    { wch: 22 },
    { wch: 18 },
    { wch: 22 },
    { wch: 22 },
    { wch: 60 },
    { wch: 28 },
    { wch: 28 },
  ];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Barcode Tracking");
  return workbook;
}

export function exportBarcodeTrackingWorkbook(rows) {
  const workbook = buildBarcodeTrackingWorkbook(rows);
  const dateStamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `barcode-tracking-${dateStamp}.xlsx`);
}
